import styles from './ScheduleOptimizerTool.module.scss';

import * as React from 'react';
import * as DateUtil from '../../util/Date';
import _ from 'lodash';

import { HealAPI } from '../../healApi';
import { useState, useEffect } from 'react';
import { PageHeader } from '../../components/core/PageHeader';
import { AddressInput, SuggestAddress } from '../../components/core/AddressInput';
import moment from 'moment-timezone';
import { Label, MessageBar, MessageBarType, Calendar, DateRangeType, DayOfWeek, Stack, Spinner, SpinnerSize, ComboBox, Dropdown } from '@fluentui/react';
import { getDateRangeArray } from '@fluentui/date-time-utilities/lib/dateMath/dateMath';
import { getZeroTimeDate } from '../../util/Date';
import { css } from '../../util/Css';
import { RootState } from '../../redux/types';
import { useSelector } from 'react-redux';
import { Timeslot } from '../../api/markets.types';

interface DateTimeslotOptions {
  proximity: Timeslot[];
  capacity: Timeslot[];
}

export const ScheduleOptimizerTool = () => {
  const [marketCode, setMarketCode] = useState('');
  const [address, setAddress] = useState<SuggestAddress>();
  const [addressStatus, setAddressStatus] = useState<{ covered: boolean; title: string; message: string }>();
  const [serviceCode, setServiceCode] = useState('SICK_ADULT');
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState<Record<string, DateTimeslotOptions>>();
  const [dataErrorMessage, setDataErrorMessage] = useState('');

  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const providers = useSelector((state: RootState) => state.providers.allProviders.data || []);

  const range = getDateRangeArray(new Date(), DateRangeType.Week, DayOfWeek.Sunday).filter((d) => d.getTime() >= getZeroTimeDate().getTime());

  const [selectedDateRange, setSelectedDateRange] = React.useState<Date[]>(range);
  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());

  const doctorOptions = [{ key: '', text: 'All providers' }].concat(
    providers
      .filter((d) => {
        if (!d.active || d.providerType !== 'DOCTOR') {
          return false;
        }
        if (!d.providerMarkets.some((m) => m.marketCode === marketCode)) {
          return false;
        }

        if (!(d as any).providerServiceViews.some((s: any) => s.code === serviceCode)) {
          return false;
        }
        return true;
      })
      .map((d) => ({ key: d.id, text: d.fullName }))
  );

  useEffect(() => {
    if (fetching) {
      setData(undefined);
      setDataErrorMessage('');
    }
  }, [fetching]);

  useEffect(() => {
    if (!address) {
      setAddressStatus(undefined);
      return;
    }
    HealAPI.checkAddressCoverage(address.latitude, address.longitude).then((response) => {
      const { covered, supportHouseCallServices, marketName, marketCode, serviceabilityDelayInSeconds } = response.data;
      if (!covered) {
        setAddressStatus({ covered, title: 'Not covered', message: 'Address is not in a Heal coverage area.' });
        setMarketCode('');
        setSelectedDoctorId('');
      } else {
        const message = `Address is covered for ${supportHouseCallServices ? 'HOUSE and TELEHEALTH' : 'TELEHEALTH'} services. Market is ${marketName} (${marketCode}).`;
        const minBooking = serviceabilityDelayInSeconds === 0 ? 'SAME DAY' : `${serviceabilityDelayInSeconds / (60 * 60 * 24)} hours`;
        setAddressStatus({ covered, title: `Covered (${minBooking})`, message });
        setMarketCode(marketCode);
        setSelectedDoctorId('');
      }
    });
  }, [address]);

  useEffect(() => {
    if (!address || !selectedDateRange || selectedDateRange.length === 0) {
      return;
    }

    setFetching(true);

    const start = moment(selectedDateRange[0]).format('YYYY-MM-DD');
    const end = moment(selectedDateRange[selectedDateRange.length - 1]).format('YYYY-MM-DD');

    HealAPI.fetchRecommendedTimeslotsForProvider(start, end, address.latitude, address.longitude, selectedDoctorId, serviceCode).then((response) => {
      if (response.ok) {
        setData(response.data);
      } else {
        setDataErrorMessage(JSON.stringify(response.data));
      }
      setFetching(false);
    });
  }, [address, selectedDateRange, serviceCode, selectedDoctorId]);

  const renderTimeslots = (date: string, slots: Timeslot[]) => {
    const sorted = _.sortBy(slots, 'startTime');
    return (
      <div key={date}>
        <Label>{moment(date).format('MM/DD/YYYY')}</Label>
        {!sorted.length && <MessageBar messageBarType={MessageBarType.info}>There are no slots on this day.</MessageBar>}
        <div className="flex">
          {sorted.map((t) => (
            <div
              className={css('ms-depth-2', styles.timeslot, { [styles.preferred]: !!data && data[date].proximity.some((tt: Timeslot) => tt.id === t.id) })}
              key={`${date}_${t.id}`}
            >
              {t.longTimeString}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSlots = () => {
    if (!data) {
      return null;
    }

    const dates = Object.keys(data).sort();
    return (
      <div className={css('ms-depth-4', styles.timeslotsContainer)}>
        <div className={css(styles.legend)}>Preferred slots because there are existing visits nearby</div>
        {dates.map((d) => renderTimeslots(d, data[d].capacity.concat(data[d].proximity)))}
      </div>
    );
  };

  const onSelectDate = (date: Date, dateRangeArray?: Date[]): void => {
    setSelectedDate(date);
    setSelectedDateRange(dateRangeArray!);
  };

  return (
    <>
      <PageHeader title="Schedule Optimizer" isLoading={fetching} />

      <div style={{ width: 500, marginBottom: 20 }}>
        <Label>Date range</Label>
        <Calendar
          onSelectDate={onSelectDate}
          strings={DateUtil.DatePickerStrings}
          minDate={new Date()}
          isMonthPickerVisible={true}
          highlightSelectedMonth={true}
          showGoToToday={false}
          dateRangeType={DateRangeType.Week}
          value={selectedDate!}
          firstDayOfWeek={DayOfWeek.Sunday}
          isDayPickerVisible={true}
        />
        <Stack horizontal tokens={{ childrenGap: 12 }}>
          <div style={{ minWidth: 500 }}>
            <Label required>Address</Label>
            <AddressInput onSelect={(address) => setAddress(address)} />
            {addressStatus && <MessageBar messageBarType={addressStatus.covered ? MessageBarType.success : MessageBarType.severeWarning}>{addressStatus.message}</MessageBar>}
          </div>
          <Dropdown
            style={{ minWidth: 200 }}
            label="Service"
            required={true}
            selectedKey={serviceCode}
            options={[
              { text: 'Sick', key: 'SICK_ADULT' },
              { text: 'Preventative', key: 'ANPS_ADULT' },
              { text: 'Telemedicine', key: 'TELEMED_ADULT' },
              { text: 'Avatar Telemedicine', key: 'AVATAR_TM' },
              { text: 'Teletherapy', key: 'THERAPY_ADULT' },
              { text: 'Flu Shot', key: 'FLUSHOT_ADULT' },
            ]}
            onChange={(e, v) => setServiceCode(v!.key as string)}
          />

          <ComboBox
            style={{ minWidth: 280 }}
            label="Filter time slots for a specific provider"
            allowFreeform={true}
            selectedKey={selectedDoctorId}
            options={doctorOptions}
            onChange={(e, o) => o && setSelectedDoctorId(o.key as string)}
          />
        </Stack>
      </div>

      {!!dataErrorMessage && <MessageBar messageBarType={MessageBarType.error}>{dataErrorMessage}</MessageBar>}

      {fetching && <Spinner style={{ marginTop: 60 }} size={SpinnerSize.large} label="Calculating..." />}

      {renderSlots()}
    </>
  );
};
